import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { motion, useScroll, useMotionValueEvent } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import LanguageSwitcher from './LanguageSwitcher';

export default function Nav({ transparentOnHero = false }) {
  const { t } = useTranslation();
  const { scrollY } = useScroll();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useMotionValueEvent(scrollY, 'change', (y) => {
    setScrolled(y > 40);
  });

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  const isTransparent = transparentOnHero && !scrolled;

  const LINKS = [
    { to: '/', key: 'home' },
    { to: '/about', key: 'about' },
    { to: '/services', key: 'services' },
    { to: '/work', key: 'work' },
    { to: '/insights', key: 'insights' },
    { to: '/contact', key: 'contact' },
  ];

  return (
    <>
      <header
        className={cn(
          'fixed top-0 left-0 right-0 z-40 transition-all duration-300',
          isTransparent
            ? 'bg-transparent'
            : 'bg-brand-background/90 backdrop-blur border-b border-black/5'
        )}
        data-testid="site-nav"
      >
        <div className="max-w-content mx-auto px-6 lg:px-10 h-16 md:h-20 flex items-center justify-between gap-4">
          <Link
            to="/"
            className={cn(
              'font-display font-semibold uppercase text-[13px] md:text-[13px] tracking-wordmark transition-colors duration-300 whitespace-nowrap',
              isTransparent ? 'text-white' : 'text-brand-primary'
            )}
            data-testid="nav-wordmark"
          >
            J Peake Consulting
          </Link>

          <nav className="hidden lg:flex items-center gap-6 xl:gap-7">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === '/'}
                className={({ isActive }) =>
                  cn(
                    'relative text-sm font-medium transition-colors duration-200 whitespace-nowrap',
                    isTransparent ? 'text-white/90 hover:text-white' : 'text-brand-primary hover:text-brand-accent',
                    isActive && 'text-brand-accent'
                  )
                }
                data-testid={`nav-link-${l.key}`}
              >
                {({ isActive }) => (
                  <>
                    {t(`nav.${l.key}`)}
                    {isActive && (
                      <span
                        className="absolute left-1/2 -translate-x-1/2 -bottom-1 w-1 h-1 rounded-full bg-brand-accent"
                        aria-hidden="true"
                      />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-5 xl:gap-6">
            <LanguageSwitcher transparent={isTransparent} testIdPrefix="nav-lang" />
            <Link
              to="/contact"
              className={cn(
                'group inline-flex items-center gap-2 border px-4 py-2 text-sm font-medium transition-colors duration-200 whitespace-nowrap',
                isTransparent
                  ? 'border-white/60 text-white hover:bg-white hover:text-brand-primary'
                  : 'border-brand-accent text-brand-accent hover:bg-brand-accent hover:text-white'
              )}
              data-testid="nav-cta-button"
            >
              {t('nav.getInTouch')}
              <span className="inline-block transition-transform duration-200 group-hover:translate-x-1">→</span>
            </Link>
          </div>

          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className={cn(
              'lg:hidden p-2 -mr-2',
              isTransparent ? 'text-white' : 'text-brand-primary'
            )}
            aria-label="Open menu"
            data-testid="nav-mobile-open"
          >
            <Menu size={24} />
          </button>
        </div>
      </header>

      {/* Mobile overlay */}
      <motion.div
        initial={false}
        animate={{ opacity: mobileOpen ? 1 : 0, pointerEvents: mobileOpen ? 'auto' : 'none' }}
        transition={{ duration: 0.25 }}
        className="fixed inset-0 z-50 bg-brand-background"
      >
        <div className="h-16 flex items-center justify-between px-6">
          <span className="font-display font-semibold uppercase text-[13px] tracking-wordmark text-brand-primary">
            J Peake Consulting
          </span>
          <button
            type="button"
            onClick={() => setMobileOpen(false)}
            className="p-2 -mr-2 text-brand-primary"
            aria-label="Close menu"
            data-testid="nav-mobile-close"
          >
            <X size={24} />
          </button>
        </div>
        <nav className="px-6 pt-6 flex flex-col gap-6">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === '/'}
              className={({ isActive }) =>
                cn(
                  'text-3xl font-display font-semibold tracking-tight',
                  isActive ? 'text-brand-accent' : 'text-brand-primary'
                )
              }
              data-testid={`nav-mobile-link-${l.key}`}
            >
              {t(`nav.${l.key}`)}
            </NavLink>
          ))}
          <Link
            to="/contact"
            className="mt-6 inline-flex items-center gap-2 border border-brand-accent text-brand-accent px-5 py-3 text-sm font-medium w-fit"
            data-testid="nav-mobile-cta"
          >
            {t('nav.getInTouch')} <span>→</span>
          </Link>
          <div className="mt-4 pt-5 border-t border-black/10">
            <LanguageSwitcher testIdPrefix="nav-mobile-lang" />
          </div>
        </nav>
      </motion.div>
    </>
  );
}
